import { buildCartCsv } from '../../utils/cartCsv';
import CartCsvImportReview, { useCartCsvImport } from '../../components/CartCsvImportReview';
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import './AlternativeCart.css';
import CashLimitPopup from '../../components/CashLimitPopup';
import CartReviewModal from '../../components/CartReviewModal';
import { BACKEND_URL } from '../../config';

const AlternativeCart = ({
  cartItems,
  removeFromCart,
  checkout,
  togglePaymentType,
  clearCart,
  userSettings,
  onSaveForLater,
  onLoadBuylist,
  setCartItems = () => {},
  importCartItems,
  calculatePrice = () => ({}),
  allowedConditions = [],
  addToCart = () => {},
  customItems = [],
  paymentSplitData,
  setPaymentSplitData
}) => {
  const isSimplistic = userSettings?.use_simplistic_template === true || userSettings?.template_version === 'simplistic';
  const PriceControl = isSimplistic ? 'button' : 'div';
  const [showCashLimitPopup, setShowCashLimitPopup] = useState(false);
  const [cashLimitConfirmed, setCashLimitConfirmed] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // Use customItems prop if provided, otherwise use local state
  const [localCustomItems, setLocalCustomItems] = useState([]);
  const [showCustomItems, setShowCustomItems] = useState(false);
  const [isLoadingCustomItems, setIsLoadingCustomItems] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  // Payment split local state
  const [isPaymentSplitExpanded, setIsPaymentSplitExpanded] = useState(false);
  const [cashAmountInput, setCashAmountInput] = useState('');
  const [creditAmountInput, setCreditAmountInput] = useState('');

  // Derived values from payment split data
  const isPriceSplitAllowed = userSettings?.priceSplit?.enabled !== false;
  const canUsePaymentSplit = isPriceSplitAllowed && typeof setPaymentSplitData === 'function';
  const cashPercentage = paymentSplitData?.cashPercentage || 0;
  const isPaymentSplitEnabled = canUsePaymentSplit && (paymentSplitData?.isEnabled || false);

  // We no longer need to use customItems in the cart as they're shown in the main content area

  // Note: quantity is always 1 now (separate line items)
  const cashSubtotal = cartItems.reduce((sum, item) => sum + (item.isCash ? parseFloat(item.cashPrice) : 0), 0);
  const creditSubtotal = cartItems.reduce((sum, item) => sum + (!item.isCash ? parseFloat(item.creditPrice) : 0), 0);
  const total = cartItems.reduce((sum, item) => sum + (item.isCash ? parseFloat(item.cashPrice) : parseFloat(item.creditPrice)), 0);
  const totalItems = cartItems.length; // Each item is a separate line now

  // Calculate total cash and credit prices for all items (regardless of current payment type selection)
  const totalCashPrice = cartItems.reduce((sum, item) => {
    return sum + parseFloat(item.cashPrice || 0);
  }, 0);
  const totalCreditPrice = cartItems.reduce((sum, item) => {
    return sum + parseFloat(item.creditPrice || 0);
  }, 0);

  // Calculate payment split amounts based on percentage (only if enabled)
  const splitCashAmount = isPaymentSplitEnabled
    ? (totalCashPrice * cashPercentage / 100)
    : 0;
  const splitCreditAmount = isPaymentSplitEnabled
    ? (totalCreditPrice * (100 - cashPercentage) / 100)
    : 0;
  const splitTotal = isPaymentSplitEnabled
    ? (splitCashAmount + splitCreditAmount)
    : 0;
  
  // Fetch custom buylist items when username changes if fetchCustomItems prop is not provided
  useEffect(() => {
    if (userSettings && userSettings.username && !customItems) {
      fetchLocalCustomItems(userSettings.username);
    }
  }, [userSettings, customItems]);
  
  // Listen for closeCustomItems and showCustomItems events
  useEffect(() => {
    const handleCloseCustomItems = () => {
      setShowCustomItems(false);
    };
    
    const handleShowCustomItems = () => {
      setShowCustomItems(true);
    };
    
    // Add event listeners to the cart element
    const cartElement = document.querySelector('.alt-cart');
    if (cartElement) {
      cartElement.addEventListener('closeCustomItems', handleCloseCustomItems);
      cartElement.addEventListener('showCustomItems', handleShowCustomItems);
    }
    
    // Clean up event listeners
    return () => {
      if (cartElement) {
        cartElement.removeEventListener('closeCustomItems', handleCloseCustomItems);
        cartElement.removeEventListener('showCustomItems', handleShowCustomItems);
      }
    };
  }, []);

  // Function to fetch custom buylist items (only used if fetchCustomItems prop is not provided)
  const fetchLocalCustomItems = async (username) => {
    try {
      setIsLoadingCustomItems(true);
      const response = await fetch(`${BACKEND_URL}/custom-buylist/${username}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setLocalCustomItems(data.items || []);
    } catch (error) {
      console.error('Error fetching custom buylist items:', error);
    } finally {
      setIsLoadingCustomItems(false);
    }
  };
  
  // Check if cash limit is exceeded
  useEffect(() => {
    // Only check if we have user settings and there are items in the cart
    if (userSettings && cartItems.length > 0) {
      const cashLimit = userSettings.cash_limit;
      
      // If cash_limit is defined, not 0, and the cash subtotal exceeds it
      if (cashLimit !== undefined && cashLimit !== 0 && cashSubtotal > cashLimit) {
        // Only show the popup if it hasn't been confirmed yet
        if (!cashLimitConfirmed) {
          setShowCashLimitPopup(true);
        }
      } else {
        // Reset if the cash subtotal goes back below the limit
        setShowCashLimitPopup(false);
      }
    }
  }, [cashSubtotal, userSettings, cartItems, cashLimitConfirmed]);
  
  const handleCashLimitPopupClose = (confirmed) => {
    setShowCashLimitPopup(false);
    if (confirmed) {
      setCashLimitConfirmed(true);
    }
  };

  // Payment Split Functions
  const updatePaymentSplit = (newCashPercentage) => {
    if (!canUsePaymentSplit) return;

    const cashPercentageDecimal = newCashPercentage / 100;
    const creditPercentageDecimal = (100 - newCashPercentage) / 100;

    const cashAmount = totalCashPrice * cashPercentageDecimal;
    const creditAmount = totalCreditPrice * creditPercentageDecimal;

    setPaymentSplitData({
      cashPercentage: newCashPercentage,
      creditPercentage: 100 - newCashPercentage,
      cashAmount,
      creditAmount,
      isEnabled: true
    });
  };

  const togglePaymentSplitSection = () => {
    if (!canUsePaymentSplit) return;

    const newExpanded = !isPaymentSplitExpanded;
    setIsPaymentSplitExpanded(newExpanded);

    if (newExpanded && !isPaymentSplitEnabled) {
      updatePaymentSplit(50);
    } else if (!newExpanded) {
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
    }
  };

  const handleCashPercentageChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      updatePaymentSplit(value);
      setCashAmountInput((totalCashPrice * value / 100).toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - value) / 100).toFixed(2));
    }
  };

  const handleCashPercentageInputChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
      setCreditAmountInput(totalCreditPrice.toFixed(2));
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      updatePaymentSplit(numValue);
      setCashAmountInput((totalCashPrice * numValue / 100).toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - numValue) / 100).toFixed(2));
    }
  };

  const handleCashPercentageInputBlur = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) {
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
      setCreditAmountInput(totalCreditPrice.toFixed(2));
    } else if (value > 100) {
      updatePaymentSplit(100);
      setCashAmountInput(totalCashPrice.toFixed(2));
      setCreditAmountInput('0.00');
    }
  };

  const handleCashAmountChange = (e) => {
    setCashAmountInput(e.target.value);
  };

  const handleCashAmountBlur = () => {
    const numValue = parseFloat(cashAmountInput);
    if (isNaN(numValue) || numValue < 0) {
      setCashAmountInput('0.00');
      updatePaymentSplit(0);
      setCreditAmountInput(totalCreditPrice.toFixed(2));
    } else if (numValue > totalCashPrice) {
      setCashAmountInput(totalCashPrice.toFixed(2));
      updatePaymentSplit(100);
      setCreditAmountInput('0.00');
    } else {
      const percentage = Math.min(100, Math.max(0, (numValue / totalCashPrice) * 100));
      updatePaymentSplit(percentage);
      setCashAmountInput(numValue.toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - percentage) / 100).toFixed(2));
    }
  };

  const handleCreditAmountChange = (e) => {
    setCreditAmountInput(e.target.value);
  };

  const handleCreditAmountBlur = () => {
    const numValue = parseFloat(creditAmountInput);
    if (isNaN(numValue) || numValue < 0) {
      setCreditAmountInput('0.00');
      updatePaymentSplit(100);
      setCashAmountInput(totalCashPrice.toFixed(2));
    } else if (numValue > totalCreditPrice) {
      setCreditAmountInput(totalCreditPrice.toFixed(2));
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
    } else {
      const creditPercentage = Math.min(100, Math.max(0, (numValue / totalCreditPrice) * 100));
      const cashPct = 100 - creditPercentage;
      updatePaymentSplit(cashPct);
      setCreditAmountInput(numValue.toFixed(2));
      setCashAmountInput((totalCashPrice * cashPct / 100).toFixed(2));
    }
  };

  // Sync amount inputs when cart changes or percentage changes
  useEffect(() => {
    if (!isPriceSplitAllowed && paymentSplitData?.isEnabled && typeof setPaymentSplitData === 'function') {
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
      setIsPaymentSplitExpanded(false);
      return;
    }

    if (isPaymentSplitEnabled) {
      setCashAmountInput((totalCashPrice * cashPercentage / 100).toFixed(2));
      setCreditAmountInput((totalCreditPrice * (100 - cashPercentage) / 100).toFixed(2));
    } else {
      setCashAmountInput('0.00');
      setCreditAmountInput('0.00');
    }
  }, [totalCashPrice, totalCreditPrice, cashPercentage, isPaymentSplitEnabled, isPriceSplitAllowed, paymentSplitData?.isEnabled, setPaymentSplitData]);

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  const currencySymbol = currencySymbols[userSettings?.currency || 'USD'] || '$';
  const currency = userSettings?.currency || 'USD';
  
  // Function to update cart item details (condition, printing, etc.)
  const updateCartItemDetails = (updatedItem) => {
    // Create a new cart array with the updated item in the same position
    const updatedCartItems = cartItems.map(item =>
      item.cartItemId === updatedItem.cartItemId ? updatedItem : item
    );

    // Use setCartItems to replace the entire cart with the updated array
    setCartItems(updatedCartItems);
  };
  
  // Reference to the file input element
  const fileInputRef = useRef(null);

  // Function to trigger the file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  const csvImport = useCartCsvImport(importCartItems);

  // Function to export cart items to CSV
  const exportToCSV = () => {
    // Skip if cart is empty
    if (cartItems.length === 0) return;

    const csvContent = buildCartCsv(cartItems, total.toFixed(2));

    // Create a Blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Set up download attributes
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    link.setAttribute('href', url);
    link.setAttribute('download', `cart-export-${date}.csv`);
    link.style.visibility = 'hidden';
    
    // Append to document, trigger download, and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Check if cash limit has been reached
  const isCashLimitReached = userSettings?.cash_limit && 
    cashSubtotal >= userSettings.cash_limit && 
    !cashLimitConfirmed;

  const allItemsCash = cartItems.length > 0 && cartItems.every(item => item.isCash);
  const allItemsCredit = cartItems.length > 0 && cartItems.every(item => !item.isCash);

  const switchAllPaymentTypes = (useCash) => {
    if (typeof togglePaymentType !== 'function') return;

    cartItems
      .filter(item => item.isCash !== useCash)
      .forEach(item => togglePaymentType(item));
  };

  return (
    <div className="alt-cart">
      {showCashLimitPopup && (
        <CashLimitPopup
          onClose={handleCashLimitPopupClose}
          cashLimit={userSettings?.cash_limit}
          cashSubtotal={cashSubtotal}
          simplistic={userSettings?.use_simplistic_template === true || userSettings?.template_version === 'simplistic'}
          currencySymbol={currencySymbol}
        />
      )}
      <div className="alt-cart-scroll-content">
      <div className="alt-cart-header">
        <div className="alt-cart-header-top">
          <h2>Your Cart</h2>
          <div className="alt-cart-info">
            <span className="alt-cart-count">{totalItems} items</span>
            <span className="alt-cart-currency">{currency}</span>
          </div>
        </div>
        <div className="alt-cart-header-buttons">
          <button
            className="alt-cart-header-button alt-load-btn"
            onClick={onLoadBuylist}
            title="Load a previously saved buylist"
          >
            📂 Load
          </button>
          <button
            className="alt-cart-header-button alt-import-btn"
            disabled={csvImport.busy}
            onClick={triggerFileInput}
            title="Import items from CSV file"
          >
            📥 Import
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".csv"
            onChange={csvImport.onFileChange}
          />
          <button
            className="alt-cart-header-button alt-export-btn"
            onClick={exportToCSV}
            disabled={cartItems.length === 0}
            title="Export cart to CSV file"
          >
            📤 Export
          </button>
          <button
            className="alt-cart-header-button alt-review-btn"
            onClick={() => setShowReviewModal(true)}
            disabled={cartItems.length === 0}
            title="Review consolidated cart"
          >
            📋 Review
          </button>
        </div>
        <div className="alt-cart-header-clear-row">
          <button
            className="alt-cart-header-button alt-clear-btn"
            onClick={clearCart}
            disabled={cartItems.length === 0}
            title="Clear all items"
          >
            🗑️ Clear
          </button>
        </div>
      </div>

      {cartItems.length > 0 && (
        <div className="alt-cart-payment-actions" aria-label="Switch all payment types">
          <button
            type="button"
            className="alt-switch-all-button alt-switch-all-cash"
            onClick={() => switchAllPaymentTypes(true)}
            disabled={allItemsCash}
            title="Switch every cart item to cash"
          >
            Switch all to Cash
          </button>
          <button
            type="button"
            className="alt-switch-all-button alt-switch-all-credit"
            onClick={() => switchAllPaymentTypes(false)}
            disabled={allItemsCredit}
            title="Switch every cart item to credit"
          >
            Switch all to Credit
          </button>
        </div>
      )}

      {/* Custom Buylist Items Button and Table removed - now shown in main content area */}
      
      <div className="alt-cart-items">
        {cartItems.length === 0 ? (
          <div className="alt-cart-empty-message">
            <p>Your cart is empty</p>
          </div>
        ) : (
          cartItems.map((item, index) => (
            <div className="alt-cart-item-wrapper" key={`wrapper-${item.cartItemId || index}`}>
              {/* Top Row: Remove, Thumbnail, Card Info */}
              <div className="alt-cart-item-top">
                <button
                  className="alt-remove-button"
                  onClick={() => removeFromCart(item)}
                  aria-label={`Remove ${String(item.name || 'item')} from cart`}
                >✕</button>

                <div className="alt-item-thumbnail">
                  {item.image ? (
                    <img src={item.image} alt={String(item.name || 'Item')} />
                  ) : (
                    <div className="alt-item-thumbnail-placeholder">{String(item.name || 'U').charAt(0)}</div>
                  )}
                </div>

                <div className="alt-item-info">
                  <div className="alt-item-name" title={String(item.name || '')}>{String(item.name || 'Unknown Item')}</div>
                  <div className="alt-item-meta">
                    {item.expansionName && <span className="alt-item-tag">{item.expansionName}</span>}
                    {item.number && <span className="alt-item-tag">#{item.number}</span>}
                    {/* Video game items show variant and console instead of condition/printing */}
                    {item.isVideoGame ? (
                      <>
                        <span className="alt-item-tag video-game">{item.variant?.toUpperCase() || 'CIB'}</span>
                        {item.consoleName && <span className="alt-item-tag console">{item.consoleName}</span>}
                      </>
                    ) : (
                      <>
                        {!item.isCustomItem && <span className="alt-item-tag">{item.condition}</span>}
                        {!item.isCustomItem && <span className="alt-item-tag">{item.printing}</span>}
                      </>
                    )}
                    {item.language && item.language !== 'EN' && <span className="alt-item-tag lang">{item.language}</span>}
                  </div>
                </div>
              </div>

              {/* Bottom Row: Price (quantity controls removed - each item is a separate line) */}
              <div className="alt-cart-item-bottom">
                <PriceControl
                  className={`alt-price-box ${item.isCash ? 'cash' : 'credit'}`}
                  type={isSimplistic ? 'button' : undefined}
                  aria-label={isSimplistic ? `Change ${item.name || 'item'} to ${item.isCash ? 'Credit' : 'Cash'}` : undefined}
                  onClick={() => {
                    if (!isCashLimitReached || item.isCash) {
                      togglePaymentType(item);
                    } else {
                      alert("Cash limit reached.");
                    }
                  }}
                >
                  <span className="alt-price-value">{currencySymbol}{(item.isCash ? item.cashPrice : item.creditPrice).toFixed(2)}</span>
                  <span className="alt-price-label">{item.isCash ? 'Cash' : 'Credit'}</span>
                  {isSimplistic && <span className="simple-payment-hint"><ArrowLeftRight size={13} aria-hidden="true" />Click to change</span>}
                </PriceControl>
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="alt-cart-summary">
        <div className="alt-summary-row">
          <span>Cash Subtotal:</span>
          <span>{currencySymbol}{cashSubtotal.toFixed(2)}</span>
        </div>
        <div className="alt-summary-row">
          <span>Credit Subtotal:</span>
          <span>{currencySymbol}{creditSubtotal.toFixed(2)}</span>
        </div>
        <div className="alt-summary-row alt-total-row">
          <span>Total:</span>
          <span>{currencySymbol}{total.toFixed(2)}</span>
        </div>
        <div className="alt-summary-note">
          <span>All prices in {currency}</span>
        </div>
      </div>

      {/* Payment Split Section - Collapsible */}
      {canUsePaymentSplit && (
        <div className="alt-payment-split-section">
          <div className="alt-payment-split-header" onClick={togglePaymentSplitSection}>
            <h4 className="alt-payment-split-title">
              Payment Split {isPaymentSplitEnabled && '(Active)'}
              <span
                className="alt-payment-split-info-icon"
                title="Split your payment between cash and store credit. Slide towards cash to receive more in cash, or towards credit to receive more in store credit."
                onClick={(e) => e.stopPropagation()}
              >
                ℹ️
              </span>
            </h4>
            <span className={`alt-collapse-icon ${isPaymentSplitExpanded ? 'expanded' : 'collapsed'}`}>
              {isPaymentSplitExpanded ? '▼' : '▶'}
            </span>
          </div>

          {isPaymentSplitExpanded && (
            <div className="alt-payment-split-content">
              <div className="alt-payment-split-slider">
                <div className="alt-slider-labels">
                  <span>Credit</span>
                  <span>Cash</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={cashPercentage}
                  onChange={handleCashPercentageChange}
                  className="alt-payment-slider"
                />
                <div className="alt-percentage-input-container">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={cashPercentage}
                    onChange={handleCashPercentageInputChange}
                    onBlur={handleCashPercentageInputBlur}
                    className="alt-percentage-input"
                  />
                  <span className="alt-percentage-symbol">%</span>
                  <span className="alt-percentage-label">Cash</span>
                </div>
              </div>

              <div className="alt-amount-inputs-container">
                <div className="alt-amount-input-group">
                  <label className="alt-amount-input-label">Cash Amount:</label>
                  <div className="alt-amount-input-wrapper">
                    <span className="alt-currency-prefix">{currencySymbol}</span>
                    <input
                      type="number"
                      min="0"
                      max={totalCashPrice}
                      step="0.01"
                      value={cashAmountInput}
                      onChange={handleCashAmountChange}
                      onBlur={handleCashAmountBlur}
                      className="alt-amount-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="alt-amount-input-group">
                  <label className="alt-amount-input-label">Credit Amount:</label>
                  <div className="alt-amount-input-wrapper">
                    <span className="alt-currency-prefix">{currencySymbol}</span>
                    <input
                      type="number"
                      min="0"
                      max={totalCreditPrice}
                      step="0.01"
                      value={creditAmountInput}
                      onChange={handleCreditAmountChange}
                      onBlur={handleCreditAmountBlur}
                      className="alt-amount-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="alt-payment-split-amounts">
                <div className="alt-split-amount">
                  <span className="alt-amount-label">Cash Amount:</span>
                  <span className="alt-amount-value">{currencySymbol}{splitCashAmount.toFixed(2)}</span>
                  <span className="alt-amount-percentage">({cashPercentage.toFixed(1)}%)</span>
                </div>
                <div className="alt-split-amount">
                  <span className="alt-amount-label">Credit Amount:</span>
                  <span className="alt-amount-value">{currencySymbol}{splitCreditAmount.toFixed(2)}</span>
                  <span className="alt-amount-percentage">({(100 - cashPercentage).toFixed(1)}%)</span>
                </div>
                <div className="alt-split-total">
                  <span className="alt-total-label">Split Total:</span>
                  <span className="alt-total-value">{currencySymbol}{splitTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      </div>

      <div className="alt-cart-actions">
        <button
          className="alt-checkout-button"
          onClick={checkout}
          disabled={cartItems.length === 0}
        >
          Proceed to Checkout
        </button>
        <button
          className="alt-save-for-later-button"
          onClick={onSaveForLater}
          disabled={cartItems.length === 0}
        >
          Save for Later
        </button>
      </div>

      <CartCsvImportReview {...csvImport.reviewProps} />

      {/* Cart Review Modal */}
      <CartReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        cartItems={cartItems}
        userSettings={userSettings}
        onSave={onSaveForLater}
      />
    </div>
  );
};

export default AlternativeCart;
